import { useEffect } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';

import { PageLoading, ThemeProvider, ToastMessageContainer } from 'enable-ui';

import { AuthLayout } from 'modules/auth/components';
import {
  type UserProfile,
  authenSelector,
  updateUserProfile,
  useAuthStore,
} from 'modules/auth/core';
import { CreatePasswordPage } from 'modules/auth/pages/CreatePassword';

import { apiCall } from 'hooks/service';
import { initAxiosInterceptor } from 'services';

import { COLOR } from 'constants/color';
import { PATH } from 'constants/path';

import { Error403Page } from './Error403Page';
import { Error404Page } from './Error404Page';
import { PrivateRoutes } from './PrivateRoutes';
import { PublicRoutes } from './PublicRoutes';

initAxiosInterceptor();

const AppRoutes = () => {
  const authen = useAuthStore(authenSelector);
  const { token } = authen;

  useEffect(() => {
    const getUserProfile = async () => {
      const result: UserProfile = await apiCall({
        url: `/auth/me`,
        method: 'GET',
        data: {
          token,
        },
        isLoading: false,
        showError: true,
      });

      if (result?.id) {
        updateUserProfile(result);
      }
    };

    if (token) {
      getUserProfile();
    }
  }, [token]);

  return (
    <ThemeProvider
      theme={{
        color: COLOR,
        typography: {
          h3: {
            size: 24,
            height: 34,
          },
          h4: {
            size: 20,
            height: 28,
          },
          'body-2': {
            size: 16,
            height: 22,
          },
          'body-3': {
            size: 14,
            height: 20,
          },
          paragraph: {
            size: 12,
            height: 16,
          },
        },
      }}
    >
      <BrowserRouter>
        <Routes>
          <Route path="error/*" element={<Outlet />}>
            <Route path="404" element={<Error404Page />} />
            <Route path="403" element={<Error403Page />} />
          </Route>
          <Route element={<AuthLayout />}>
            <Route path={PATH.CREATE_PASSWORD} element={<CreatePasswordPage />} />
          </Route>
          <Route path="*" element={token ? <PrivateRoutes /> : <PublicRoutes />} />
        </Routes>
        <ToastMessageContainer autoClose={1500} hideProgressBar={false} />
        <PageLoading zIndex={999999} />
      </BrowserRouter>
    </ThemeProvider>
  );
};

export { AppRoutes };
